import { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Camera, Loader2, Undo2, CheckCircle2, PartyPopper } from "lucide-react";
import { toast } from "sonner";

const SIDES = ["front", "back", "left", "right"];
const PONDY = { lat: 11.9139, long: 79.8145 };

function Row({ label, value, strong }) {
  return (
    <div className={`flex items-center justify-between py-1 text-sm ${strong ? "font-semibold" : ""}`}>
      <span className={strong ? "" : "text-muted-foreground"}>{label}</span>
      <span>₹{(value || 0).toLocaleString()}</span>
    </div>
  );
}

export default function ReturnWizard({ booking, onDone }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState([]);
  const [step, setStep] = useState("return"); // return | settle | done
  const [settlement, setSettlement] = useState(null);
  const [r, setR] = useState({ photos: [], odometer_in: "", charge_in: "", damage_keys: [], staff_confirm: false });

  useEffect(() => {
    if (open) api.get("/operator/damage-items").then(({ data }) => setItems(data)).catch(() => {});
  }, [open]);

  const handleError = (e) => toast.error(formatApiError(e.response?.data?.detail) || "Error");

  const submitReturn = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/operator/bookings/${booking.id}/return`, {
        photo_sides: r.photos, photo_geo: r.photos.length ? PONDY : {},
        odometer_in: Number(r.odometer_in), charge_in: Number(r.charge_in),
        damage_keys: r.damage_keys, staff_confirm: r.staff_confirm,
      });
      setSettlement(data.settlement); setStep("settle");
    } catch (e) { handleError(e); } finally { setBusy(false); }
  };

  const submitSettle = async () => {
    setBusy(true);
    try {
      await api.post(`/operator/bookings/${booking.id}/settle`);
      setStep("done"); toast.success("Rental closed & deposit reconciled");
    } catch (e) { handleError(e); } finally { setBusy(false); }
  };

  const close = (o) => {
    setOpen(o);
    if (!o) { setStep("return"); setSettlement(null); setR({ photos: [], odometer_in: "", charge_in: "", damage_keys: [], staff_confirm: false }); onDone?.(); }
  };

  const toggleDamage = (key) =>
    setR((s) => ({ ...s, damage_keys: s.damage_keys.includes(key) ? s.damage_keys.filter((k) => k !== key) : [...s.damage_keys, key] }));

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogTrigger asChild>
        <Button data-testid={`return-btn-${booking.id}`} size="sm" variant="outline" className="gap-1.5">
          <Undo2 className="h-3.5 w-3.5" /> Return & settle
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Return & settle — {booking.customer_name}</DialogTitle>
          <DialogDescription>{booking.vehicle_reg} · {booking.vehicle_model}</DialogDescription>
        </DialogHeader>

        {step === "return" && (
          <div className="space-y-4">
            <div>
              <Label className="mb-1.5 block">Return condition photos (all sides)</Label>
              <div className="grid grid-cols-4 gap-2">
                {SIDES.map((side) => {
                  const captured = r.photos.includes(side);
                  return (
                    <button key={side} type="button" data-testid={`return-photo-${side}`}
                      onClick={() => setR((s) => ({ ...s, photos: captured ? s.photos.filter((p) => p !== side) : [...s.photos, side] }))}
                      className={`flex flex-col items-center gap-1 rounded-lg border py-3 text-xs capitalize ${captured ? "border-tenant-primary bg-tenant-primary/10 text-tenant-primary" : "border-border text-muted-foreground"}`}>
                      <Camera className="h-4 w-4" /> {side}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Odometer in (km)</Label>
                <Input data-testid="return-odometer" type="number" value={r.odometer_in} onChange={(e) => setR({ ...r, odometer_in: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Charge in %</Label>
                <Input data-testid="return-charge" type="number" value={r.charge_in} onChange={(e) => setR({ ...r, charge_in: e.target.value })} /></div>
            </div>
            <div className="rounded-lg border border-border p-3">
              <p className="mb-2 text-sm font-medium">Damage (charged against deposit)</p>
              <div className="space-y-1.5">
                {items.map((it) => (
                  <label key={it.key} className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2">
                      <input type="checkbox" data-testid={`return-damage-${it.key}`} checked={r.damage_keys.includes(it.key)} onChange={() => toggleDamage(it.key)} />
                      {it.part}
                    </span>
                    <span className="text-muted-foreground">₹{it.repair_cost.toLocaleString()}</span>
                  </label>
                ))}
              </div>
            </div>
            <label className="flex items-center justify-between rounded-lg border border-border p-3 text-sm">
              Staff confirmation
              <Switch data-testid="return-staff-confirm" checked={r.staff_confirm} onCheckedChange={(v) => setR({ ...r, staff_confirm: v })} />
            </label>
            <Button data-testid="return-submit" onClick={submitReturn} disabled={busy} className="w-full gap-2">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Compute settlement
            </Button>
          </div>
        )}

        {step === "settle" && settlement && (
          <div className="space-y-4" data-testid="settlement-summary">
            <div className="rounded-lg border border-border p-4">
              <p className="mb-2 text-sm font-medium">Settlement</p>
              <Row label={`Km used (${settlement.km_used} / cap ${settlement.km_cap})`} value={0} />
              <Row label={`Overage (${settlement.overage_km} km)`} value={settlement.overage_charge} />
              <Row label="Damage" value={settlement.damage_total} />
              <Row label="Low-charge penalty" value={settlement.low_charge_penalty} />
              <div className="my-2 border-t border-border" />
              <Row label="Total deductions" value={settlement.deductions} strong />
              <Row label="Deposit held" value={settlement.deposit_held} />
              <div className="my-2 border-t border-border" />
              {settlement.balance_due > 0 ? (
                <div className="flex items-center justify-between text-sm font-semibold text-status-recovery">
                  <span>Balance due from rider</span><span>₹{settlement.balance_due.toLocaleString()}</span>
                </div>
              ) : (
                <div className="flex items-center justify-between text-sm font-semibold text-status-available">
                  <span>Refund to rider</span><span>₹{settlement.refund.toLocaleString()}</span>
                </div>
              )}
            </div>
            <Button data-testid="settle-submit" onClick={submitSettle} disabled={busy} className="w-full gap-2">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} <CheckCircle2 className="h-4 w-4" /> Confirm & close rental
            </Button>
          </div>
        )}

        {step === "done" && (
          <div className="flex flex-col items-center gap-3 py-6 text-center" data-testid="settle-done">
            <PartyPopper className="h-10 w-10 text-tenant-primary" />
            <p className="text-lg font-semibold">Rental closed</p>
            <p className="text-sm text-muted-foreground">Deposit reconciled, reliability updated, vehicle released.</p>
            <Button data-testid="settle-done-btn" onClick={() => close(false)} className="mt-2">Done</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
